import { AxiosPromise } from 'axios';

import {
  Country,
  CountryPeopleListItem,
  CountryPetsListItem,
  CountryScope,
  GetCountriesParams,
  PaginatedResponse,
  UpdateCountrySectionDto,
} from 'shared/api';
import { apiInstance } from 'shared/api/typicode/base';

const BASE_URL = '/country';

/**
 * Получение полного списка всех стран (для селекторов в диалогах)
 * @returns Promise с ответом, содержащим массив всех стран
 */
export const getCountries = async (): AxiosPromise<Country[]> =>
  apiInstance.get(BASE_URL, {
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
    },
  });

/**
 * Получение списка стран с пагинацией и группировкой заболеваний (для людей)
 * @param params - Параметры запроса (фильтрация, сортировка, пагинация)
 * @returns Promise с ответом, содержащим массив стран и информацию о пагинации
 */
export const getCountriesPeople = async (
  params?: GetCountriesParams,
): AxiosPromise<PaginatedResponse<CountryPeopleListItem>> =>
  apiInstance.get(`${BASE_URL}/people/all`, {
    params,
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
    },
  });

/**
 * Получение списка стран с пагинацией и группировкой заболеваний (для питомцев)
 * @param params - Параметры запроса (фильтрация, сортировка, пагинация)
 * @returns Promise с ответом, содержащим массив стран и информацию о пагинации
 */
export const getCountriesPets = async (
  params?: GetCountriesParams,
): AxiosPromise<PaginatedResponse<CountryPetsListItem>> =>
  apiInstance.get(`${BASE_URL}/pets/all`, {
    params,
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
    },
  });

/** Элемент списка стран — форма ответа у people и pets совпадает по нужным нам полям */
type CountrySectionResponse = CountryPeopleListItem | CountryPetsListItem;

/**
 * Обновление паспорта страны: описание, ссылки, отметка о проверке.
 * links передаются полным массивом — на беке это замена, а не патч.
 * @param scope - 'people' или 'pets'
 * @param code - ISO alpha-2 код страны
 * @param dto - изменяемые поля паспорта
 */
export const updateCountrySection = async (
  scope: CountryScope,
  code: string,
  dto: UpdateCountrySectionDto,
): AxiosPromise<CountrySectionResponse> =>
  apiInstance.patch(`${BASE_URL}/${scope}/${code}`, dto, {
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
    },
  });

/**
 * Загрузка вложения к паспорту страны. Бек принимает поле files и до 10 файлов,
 * но по продуктовому решению отправляем ровно один.
 * @param scope - 'people' или 'pets'
 * @param code - ISO alpha-2 код страны
 * @param file - файл нацкалендаря
 */
export const uploadCountryAttachment = async (
  scope: CountryScope,
  code: string,
  file: File,
): AxiosPromise<CountrySectionResponse> => {
  const formData = new FormData();
  formData.append('files', file);

  return apiInstance.post(`${BASE_URL}/${scope}/${code}/attachments`, formData, {
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Удаление вложения из паспорта страны
 * @param scope - 'people' или 'pets'
 * @param code - ISO alpha-2 код страны
 * @param fileId - id из info.attachments[].id
 */
export const deleteCountryAttachment = async (
  scope: CountryScope,
  code: string,
  fileId: string,
): AxiosPromise<CountrySectionResponse> =>
  apiInstance.delete(`${BASE_URL}/${scope}/${code}/attachments/${fileId}`, {
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem('token') ?? ''}`,
    },
  });
